import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("llm_hub_token"));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  // Set up axios interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem("llm_hub_token");
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes("/auth/")) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const fetchMe = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem("llm_hub_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.requires_2fa) {
      setRequires2FA(true);
      setTempToken(res.data.temp_token);
      return { requires2FA: true };
    }
    localStorage.setItem("llm_hub_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return { requires2FA: false };
  };

  const verify2FA = async (code) => {
    const res = await axios.post(`${API}/auth/verify-2fa`, { code }, {
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    localStorage.setItem("llm_hub_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    setRequires2FA(false);
    setTempToken(null);
  };

  const logout = () => {
    localStorage.removeItem("llm_hub_token");
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setTempToken(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    return user.permissions?.[perm] === true;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, verify2FA, requires2FA, tempToken, hasPermission, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
